import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export type PracticeGender = 'male' | 'female' | 'unspecified';

export type PracticeRotationParticipant = {
  id: string;
  playerId: string | null;
  name: string;
  gender: PracticeGender;
  groupMeName?: string | null;
  matched: boolean;
  ranking?: number | null;
};

export type PracticeRotationPairRule = {
  player1Id: string;
  player2Id: string;
};

export type PracticeRotationAvailabilityRule = {
  playerId: string;
  round: number;
};

export type PracticeStrengthMix = 'balanced' | 'mixed' | 'development' | 'random';

export type PracticeRotationSettings = {
  courts: number;
  courtLabels: string[];
  durationMinutes: number;
  warmupMinutes: number;
  roundDurationMinutes: number | 'auto';
  strengthMix: PracticeStrengthMix;
  prepareUpcomingMatchPairings: boolean;
  usePartnerPreferences: boolean;
  preferMixedTeams: boolean;
  sitFirstPlayerIds: string[];
  noSitPlayerIds: string[];
  sitLastPlayerIds: string[];
  arriveLate: PracticeRotationAvailabilityRule[];
  leaveEarly: PracticeRotationAvailabilityRule[];
  mustPartner: PracticeRotationPairRule[];
  avoidPartner: PracticeRotationPairRule[];
  weights: PracticeRotationWeights;
};

export type PracticeRotationWeights = {
  courtUtilization: number;
  equalPlayingTime: number;
  sitOutFairness: number;
  avoidConsecutiveSits: number;
  partnerVariety: number;
  mixedPairing: number;
  upcomingMatchPairing: number;
  partnerPreference: number;
};

export type PracticeRotationConfig = {
  algorithmVersion: string;
  defaultSettings: PracticeRotationSettings;
};

export type PracticePartnerPreference = {
  player1Id: string;
  player2Id: string;
  compatibility?: string | null;
  pairingRule?: string | null;
};

export type PracticeRotationInput = {
  participants: PracticeRotationParticipant[];
  settings: PracticeRotationSettings;
  upcomingMatchPairs?: PracticeRotationPairRule[];
  partnerPreferences?: PracticePartnerPreference[];
  eventName?: string;
  eventDate?: string | null;
  teamName?: string | null;
  seed?: string;
};

export type PracticeRotationTeam = {
  players: PracticeRotationParticipant[];
  source: 'must_partner' | 'upcoming_match' | 'partner_preference' | 'mixed' | 'balanced' | 'single';
};

export type PracticeRotationCourt = {
  courtNumber: number;
  courtLabel: string;
  teamA: PracticeRotationTeam | null;
  teamB: PracticeRotationTeam | null;
  note?: string | null;
};

export type PracticeRotationRound = {
  roundNumber: number;
  startMinute: number;
  endMinute: number;
  sitting: PracticeRotationParticipant[];
  courts: PracticeRotationCourt[];
};

export type PracticeRotationMetrics = {
  participantCount: number;
  matchedParticipantCount: number;
  unmatchedParticipantCount: number;
  courts: number;
  roundDurationMinutes: number;
  roundCount: number;
  warmupMinutes: number;
  rotationMinutes: number;
  scheduledMinutes: number;
  playCountMin: number;
  playCountMax: number;
  sitCountMin: number;
  sitCountMax: number;
  maxConsecutiveSits: number;
  courtUtilizationPercent: number;
  mixedTeamPercent: number;
  uniquePartnerPercent: number;
  upcomingPairsPracticed: number;
  upcomingPairsAvailable: number;
  qualityScore: number;
};

export type PracticeRotationPlan = {
  algorithmVersion: string;
  generatedAt: string;
  eventName?: string;
  eventDate?: string | null;
  teamName?: string | null;
  participantSnapshot: PracticeRotationParticipant[];
  rsvpSnapshotHash: string;
  settingsSnapshot: PracticeRotationSettings;
  rounds: PracticeRotationRound[];
  metrics: PracticeRotationMetrics;
  warnings: string[];
};

export type PracticeRotationValidationResult = {
  valid: boolean;
  errors: string[];
};

export const defaultPracticeRotationWeights: PracticeRotationWeights = {
  courtUtilization: 22,
  equalPlayingTime: 18,
  sitOutFairness: 18,
  avoidConsecutiveSits: 12,
  partnerVariety: 12,
  mixedPairing: 8,
  upcomingMatchPairing: 6,
  partnerPreference: 4,
};

export const defaultPracticeRotationSettings: PracticeRotationSettings = {
  courts: 3,
  courtLabels: ['1', '2', '3'],
  durationMinutes: 90,
  warmupMinutes: 10,
  roundDurationMinutes: 12,
  strengthMix: 'mixed',
  prepareUpcomingMatchPairings: true,
  usePartnerPreferences: true,
  preferMixedTeams: true,
  sitFirstPlayerIds: [],
  noSitPlayerIds: [],
  sitLastPlayerIds: [],
  arriveLate: [],
  leaveEarly: [],
  mustPartner: [],
  avoidPartner: [],
  weights: defaultPracticeRotationWeights,
};

export const defaultPracticeRotationConfig: PracticeRotationConfig = {
  algorithmVersion: 'practice_rotation_v1',
  defaultSettings: defaultPracticeRotationSettings,
};

export const practiceStrengthMixDescriptions: Record<PracticeStrengthMix, string> = {
  balanced: 'Keeps partners and courts closer in strength when ratings are available.',
  mixed: 'Default. Creates varied matchups while keeping courts reasonably competitive.',
  development: 'Leans into stronger cross-skill exposure while avoiding repeated unfair courts.',
  random: 'Ignores player strength and lets variety and captain constraints drive the plan.',
};

export function resizePracticeCourtLabels(labels: string[] | null | undefined, courtCount: number): string[] {
  const normalizedCount = Math.min(10, Math.max(1, Number(courtCount || defaultPracticeRotationSettings.courts)));
  const existing = Array.isArray(labels) ? labels.map((label) => String(label || '').trim()) : [];
  return Array.from({ length: normalizedCount }, (_, index) => existing[index] || String(index + 1));
}

export function normalizePracticeCourtLabels(labels: string[] | null | undefined, courtCount: number): string[] {
  return resizePracticeCourtLabels(labels, courtCount);
}

export function displayPracticeCourtLabel(label: string | number | null | undefined): string {
  const value = String(label ?? '').trim();
  if (!value) return 'Court';
  return /^court\b/i.test(value) ? value : `Court ${value}`;
}

function normalizedGender(value: string | null | undefined): PracticeGender {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'male' || normalized === 'm') return 'male';
  if (normalized === 'female' || normalized === 'f') return 'female';
  return 'unspecified';
}

export function toPracticeGender(value: string | null | undefined): PracticeGender {
  return normalizedGender(value);
}

export function practiceParticipantKey(participant: Pick<PracticeRotationParticipant, 'id' | 'playerId' | 'name'>): string {
  return participant.playerId || participant.id || participant.name;
}

function pairKey(a: string | null | undefined, b: string | null | undefined): string {
  const values = [a || '', b || ''].sort();
  return `${values[0]}::${values[1]}`;
}

function hasPair(ruleSet: Set<string>, a: PracticeRotationParticipant, b: PracticeRotationParticipant): boolean {
  return Boolean(a.playerId && b.playerId && ruleSet.has(pairKey(a.playerId, b.playerId)));
}

function normalizeSettings(settings: Partial<PracticeRotationSettings> | null | undefined): PracticeRotationSettings {
  const courts = Math.min(10, Math.max(1, Number(settings?.courts || defaultPracticeRotationSettings.courts)));
  const durationMinutes = Math.min(180, Math.max(30, Number(settings?.durationMinutes || defaultPracticeRotationSettings.durationMinutes)));
  const warmupMinutes = Math.min(60, Math.max(0, Number(settings?.warmupMinutes ?? defaultPracticeRotationSettings.warmupMinutes)));
  const strengthMix = ['balanced', 'mixed', 'development', 'random'].includes(String(settings?.strengthMix || ''))
    ? settings?.strengthMix as PracticeStrengthMix
    : defaultPracticeRotationSettings.strengthMix;
  return {
    ...defaultPracticeRotationSettings,
    ...(settings || {}),
    courts,
    courtLabels: normalizePracticeCourtLabels(settings?.courtLabels, courts),
    durationMinutes,
    warmupMinutes,
    roundDurationMinutes: settings?.roundDurationMinutes === 'auto'
      ? chooseRoundDuration({
        ...defaultPracticeRotationSettings,
        ...(settings || {}),
        courts,
        courtLabels: normalizePracticeCourtLabels(settings?.courtLabels, courts),
        durationMinutes,
        warmupMinutes,
        strengthMix,
      } as PracticeRotationSettings, 0)
      : Math.min(60, Math.max(5, Number(settings?.roundDurationMinutes || defaultPracticeRotationSettings.roundDurationMinutes))),
    strengthMix,
    weights: { ...defaultPracticeRotationWeights, ...(settings?.weights || {}) },
    sitFirstPlayerIds: settings?.sitFirstPlayerIds || [],
    noSitPlayerIds: settings?.noSitPlayerIds || [],
    sitLastPlayerIds: settings?.sitLastPlayerIds || [],
    arriveLate: settings?.arriveLate || [],
    leaveEarly: settings?.leaveEarly || [],
    mustPartner: settings?.mustPartner || [],
    avoidPartner: settings?.avoidPartner || [],
  };
}

export function practiceRsvpSnapshotHash(participants: PracticeRotationParticipant[]): string {
  const stable = participants
    .map((participant) => ({
      key: practiceParticipantKey(participant),
      name: participant.name,
      gender: participant.gender,
      matched: participant.matched,
    }))
    .sort((a, b) => a.key.localeCompare(b.key) || a.name.localeCompare(b.name));
  const text = JSON.stringify(stable);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function practiceRsvpSnapshotDifferences(previous: PracticeRotationParticipant[], current: PracticeRotationParticipant[]): string[] {
  const previousMap = new Map(previous.map((participant) => [practiceParticipantKey(participant), participant]));
  const currentMap = new Map(current.map((participant) => [practiceParticipantKey(participant), participant]));
  const differences: string[] = [];
  currentMap.forEach((participant, key) => {
    if (!previousMap.has(key)) differences.push(`${participant.name} is now going`);
  });
  previousMap.forEach((participant, key) => {
    if (!currentMap.has(key)) differences.push(`${participant.name} is no longer going`);
  });
  return differences.sort((a, b) => a.localeCompare(b));
}

function isAvailableForRound(playerId: string | null, roundNumber: number, settings: PracticeRotationSettings): boolean {
  if (!playerId) return true;
  const late = settings.arriveLate.find((rule) => rule.playerId === playerId);
  if (late && roundNumber < late.round) return false;
  const early = settings.leaveEarly.find((rule) => rule.playerId === playerId);
  if (early && roundNumber > early.round) return false;
  return true;
}

function chooseRoundDuration(settings: PracticeRotationSettings, participantCount: number): number {
  if (settings.roundDurationMinutes !== 'auto') return Number(settings.roundDurationMinutes);
  const candidates = [20, 15, 12, 10];
  const capacity = settings.courts * 4;
  const rotationMinutes = Math.max(5, settings.durationMinutes - settings.warmupMinutes);
  let best = candidates[0];
  let bestScore = -Infinity;
  candidates.forEach((duration) => {
    const rounds = Math.floor(rotationMinutes / duration);
    if (rounds <= 0) return;
    const totalSlots = rounds * capacity;
    const averagePlays = participantCount > 0 ? totalSlots / participantCount : 0;
    const leftover = rotationMinutes - rounds * duration;
    const score = -Math.abs(averagePlays - Math.round(averagePlays)) * 30
      - leftover * 0.4
      + (duration === 12 ? 8 : 0)
      + (rounds >= 5 ? 4 : 0)
      - (rounds > 10 ? 10 : 0);
    if (score > bestScore) {
      best = duration;
      bestScore = score;
    }
  });
  return best;
}

function sortedParticipants(participants: PracticeRotationParticipant[]): PracticeRotationParticipant[] {
  return [...participants].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

function seededRandomScore(seed: string, key: string): number {
  const text = `${seed}:${key}`;
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}

function chooseSitters(
  available: PracticeRotationParticipant[],
  roundNumber: number,
  settings: PracticeRotationSettings,
  capacity: number,
  playCounts: Map<string, number>,
  sitCounts: Map<string, number>,
  previousSitKeys: Set<string>,
  tiebreakerSeed: string,
  warnings: string[],
): PracticeRotationParticipant[] {
  const sitNeeded = Math.max(0, available.length - capacity);
  if (sitNeeded === 0) return [];
  const forced = roundNumber === 1
    ? available.filter((participant) => participant.playerId && settings.sitFirstPlayerIds.includes(participant.playerId))
    : [];
  const sitters = new Map(forced.slice(0, sitNeeded).map((participant) => [practiceParticipantKey(participant), participant]));
  const remainingSitNeeded = sitNeeded - sitters.size;
  const noSitIds = new Set(settings.noSitPlayerIds);
  const protectedCandidateCount = available.filter((participant) => participant.playerId && noSitIds.has(participant.playerId) && !sitters.has(practiceParticipantKey(participant))).length;
  const eligibleNonProtectedCount = available.length - sitters.size - protectedCandidateCount;
  const mustUseProtected = eligibleNonProtectedCount < remainingSitNeeded;
  if (mustUseProtected && protectedCandidateCount > 0) {
    warnings.push('One or more Do Not Sit players had to sit because there were not enough court spots for all protected players.');
  }
  const candidates = available.filter((participant) => {
    if (sitters.has(practiceParticipantKey(participant))) return false;
    return mustUseProtected || !participant.playerId || !noSitIds.has(participant.playerId);
  });
  const sitLastIds = new Set(settings.sitLastPlayerIds);

  const knownGenderCounts = (players: PracticeRotationParticipant[]) => players.reduce(
    (counts, participant) => ({
      male: counts.male + (participant.gender === 'male' ? 1 : 0),
      female: counts.female + (participant.gender === 'female' ? 1 : 0),
    }),
    { male: 0, female: 0 },
  );
  const totalKnownGenderCounts = knownGenderCounts(available);
  const mixedBalanceScore = (candidate: PracticeRotationParticipant): number => {
    if (!settings.preferMixedTeams) return 0;
    if (candidate.gender !== 'male' && candidate.gender !== 'female') return 0;
    const projectedSitters = [...sitters.values(), candidate];
    const sittingKnownCounts = knownGenderCounts(projectedSitters);
    const malePlaying = totalKnownGenderCounts.male - sittingKnownCounts.male;
    const femalePlaying = totalKnownGenderCounts.female - sittingKnownCounts.female;
    const mixedKnownSlots = Math.min(malePlaying, femalePlaying) * 2;
    const knownImbalance = Math.abs(malePlaying - femalePlaying);
    return mixedKnownSlots * settings.weights.mixedPairing * 6 - knownImbalance * settings.weights.mixedPairing * 8;
  };
  const fairnessScore = (participant: PracticeRotationParticipant): number => {
    const key = practiceParticipantKey(participant);
    const consecutivePenalty = previousSitKeys.has(key) ? -4 : 0;
    const sitLastPenalty = participant.playerId && sitLastIds.has(participant.playerId) ? -100 : 0;
    return (playCounts.get(key) || 0) * 3 - (sitCounts.get(key) || 0) * 4 + consecutivePenalty + sitLastPenalty;
  };

  while (sitters.size < sitNeeded && candidates.length > 0) {
    candidates.sort((a, b) => {
      const aScore = mixedBalanceScore(a) + fairnessScore(a);
      const bScore = mixedBalanceScore(b) + fairnessScore(b);
      const aRandom = seededRandomScore(tiebreakerSeed, `${roundNumber}:${practiceParticipantKey(a)}`);
      const bRandom = seededRandomScore(tiebreakerSeed, `${roundNumber}:${practiceParticipantKey(b)}`);
      return bScore - aScore || bRandom - aRandom || a.name.localeCompare(b.name);
    });
    const next = candidates.shift();
    if (next) sitters.set(practiceParticipantKey(next), next);
  }
  return [...sitters.values()];
}

function preferenceScore(preference: PracticePartnerPreference | undefined, settings: PracticeRotationSettings): number {
  if (!settings.usePartnerPreferences || !preference) return 0;
  const rule = (preference.pairingRule || '').toLowerCase();
  const compatibility = (preference.compatibility || '').toLowerCase();
  if (rule === 'strongly_preferred') return settings.weights.partnerPreference * 4;
  if (rule === 'preferred') return settings.weights.partnerPreference * 2;
  if (compatibility === 'excellent') return settings.weights.partnerPreference * 2;
  if (compatibility === 'good') return settings.weights.partnerPreference;
  if (compatibility === 'poor') return -settings.weights.partnerPreference * 2;
  return 0;
}

function rankingValue(participant: PracticeRotationParticipant): number | null {
  const value = Number(participant.ranking);
  return Number.isFinite(value) ? value : null;
}

function teamAverageRanking(team: PracticeRotationTeam | null): number | null {
  const rankings = (team?.players || []).map(rankingValue).filter((value): value is number => value != null);
  if (rankings.length === 0) return null;
  return rankings.reduce((sum, value) => sum + value, 0) / rankings.length;
}

function teamRankingSpread(team: PracticeRotationTeam | null): number {
  const rankings = (team?.players || []).map(rankingValue).filter((value): value is number => value != null);
  if (rankings.length < 2) return 0;
  return Math.max(...rankings) - Math.min(...rankings);
}

function strengthPairScore(
  player: PracticeRotationParticipant,
  candidate: PracticeRotationParticipant,
  settings: PracticeRotationSettings,
  rankRange: number,
): number {
  if (settings.strengthMix === 'random') return 0;
  const playerRank = rankingValue(player);
  const candidateRank = rankingValue(candidate);
  if (playerRank == null || candidateRank == null) return 0;
  const normalizedDifference = Math.abs(playerRank - candidateRank) / Math.max(rankRange, 0.25);
  if (settings.strengthMix === 'balanced') return -normalizedDifference * 10;
  if (settings.strengthMix === 'development') return normalizedDifference * 14;
  return normalizedDifference * 8;
}

function makeTeams(
  playing: PracticeRotationParticipant[],
  settings: PracticeRotationSettings,
  upcomingPairSet: Set<string>,
  mustPairSet: Set<string>,
  avoidPairSet: Set<string>,
  preferences: Map<string, PracticePartnerPreference>,
  partnerCounts: Map<string, number>,
  warnings: string[],
): PracticeRotationTeam[] {
  const remaining = sortedParticipants(playing);
  const teams: PracticeRotationTeam[] = [];
  const prioritizeVariety = !settings.prepareUpcomingMatchPairings && !settings.usePartnerPreferences;
  const rankings = remaining.map(rankingValue).filter((value): value is number => value != null);
  const rankRange = rankings.length ? Math.max(...rankings) - Math.min(...rankings) : 0;

  const pairScore = (player: PracticeRotationParticipant, candidate: PracticeRotationParticipant): number => {
    if (hasPair(avoidPairSet, player, candidate)) return -Infinity;
    const key = pairKey(player.playerId, candidate.playerId);
    const repeatCount = partnerCounts.get(key) || 0;
    let score = 0;
    if (mustPairSet.has(key)) score += 1000;
    if (settings.prepareUpcomingMatchPairings && upcomingPairSet.has(key)) score += settings.weights.upcomingMatchPairing * 8;
    score += preferenceScore(preferences.get(key), settings);
    if (settings.preferMixedTeams && player.gender !== 'unspecified' && candidate.gender !== 'unspecified' && player.gender !== candidate.gender) score += settings.weights.mixedPairing * 4;
    score += strengthPairScore(player, candidate, settings, rankRange);
    score -= repeatCount * settings.weights.partnerVariety * (prioritizeVariety ? 40 : 1);
    score += Math.abs(player.name.charCodeAt(0) - candidate.name.charCodeAt(0)) / 1000;
    return score;
  };

  const signatureForTeams = (candidateTeams: PracticeRotationTeam[]): string => candidateTeams
    .map((team) => team.players.map((player) => player.name).sort().join('::'))
    .sort()
    .join('||');

  const sourceForPair = (player: PracticeRotationParticipant, partner: PracticeRotationParticipant): PracticeRotationTeam['source'] => {
    const key = pairKey(player.playerId, partner.playerId);
    if (mustPairSet.has(key)) return 'must_partner';
    if (settings.prepareUpcomingMatchPairings && upcomingPairSet.has(key)) return 'upcoming_match';
    if (settings.usePartnerPreferences && preferences.has(key)) return 'partner_preference';
    if (settings.preferMixedTeams && player.gender !== partner.gender) return 'mixed';
    return 'balanced';
  };

  const bestRemainingPairScore = (candidates: PracticeRotationParticipant[]): number => {
    let bestScore = 0;
    for (let playerIndex = 0; playerIndex < candidates.length; playerIndex += 1) {
      for (let partnerIndex = playerIndex + 1; partnerIndex < candidates.length; partnerIndex += 1) {
        bestScore = Math.max(bestScore, pairScore(candidates[playerIndex], candidates[partnerIndex]));
      }
    }
    return bestScore;
  };

  const buildGreedyTeamSet = (candidates: PracticeRotationParticipant[]): PracticeRotationTeam[] => {
    const queue = [...candidates];
    const candidateTeams: PracticeRotationTeam[] = [];
    while (queue.length > 1) {
      let best: { player: PracticeRotationParticipant; partner: PracticeRotationParticipant; score: number; signature: string } | null = null;
      for (let playerIndex = 0; playerIndex < queue.length; playerIndex += 1) {
        for (let partnerIndex = playerIndex + 1; partnerIndex < queue.length; partnerIndex += 1) {
          const player = queue[playerIndex];
          const partner = queue[partnerIndex];
          const score = pairScore(player, partner);
          if (score === -Infinity) continue;
          const remainingAfterPair = queue.filter((candidate) => candidate !== player && candidate !== partner);
          const totalScore = score + bestRemainingPairScore(remainingAfterPair);
          const signature = [player.name, partner.name].sort().join('::');
          if (!best || totalScore > best.score || (totalScore === best.score && signature.localeCompare(best.signature) < 0)) {
            best = { player, partner, score: totalScore, signature };
          }
        }
      }
      if (!best) break;
      queue.splice(queue.indexOf(best.player), 1);
      queue.splice(queue.indexOf(best.partner), 1);
      candidateTeams.push({ players: [best.player, best.partner], source: sourceForPair(best.player, best.partner) });
    }
    queue.forEach((player) => candidateTeams.push({ players: [player], source: 'single' }));
    return candidateTeams;
  };

  const memo = new Map<string, { teams: PracticeRotationTeam[]; score: number; signature: string }>();
  const buildBestTeamSet = (
    candidates: PracticeRotationParticipant[],
  ): { teams: PracticeRotationTeam[]; score: number; signature: string } => {
    if (candidates.length > 14) {
      const candidateTeams = buildGreedyTeamSet(candidates);
      return { teams: candidateTeams, score: 0, signature: signatureForTeams(candidateTeams) };
    }
    if (candidates.length === 0) return { teams: [], score: 0, signature: '' };
    if (candidates.length === 1) {
      const singleTeam: PracticeRotationTeam = { players: [candidates[0]], source: 'single' };
      return { teams: [singleTeam], score: 0, signature: signatureForTeams([singleTeam]) };
    }
    const memoKey = candidates.map(practiceParticipantKey).sort().join('||');
    const memoized = memo.get(memoKey);
    if (memoized) return memoized;

    const player = candidates[0];
    let best: { teams: PracticeRotationTeam[]; score: number; signature: string } | null = null;
    for (let partnerIndex = 1; partnerIndex < candidates.length; partnerIndex += 1) {
      const partner = candidates[partnerIndex];
      const score = pairScore(player, partner);
      if (score === -Infinity) continue;
      const source = sourceForPair(player, partner);

      const nextCandidates = candidates.filter((candidate) => candidate !== player && candidate !== partner);
      const tail = buildBestTeamSet(nextCandidates);
      const candidateTeams = [{ players: [player, partner], source }, ...tail.teams];
      const totalScore = score + tail.score;
      const signature = signatureForTeams(candidateTeams);
      if (!best || totalScore > best.score || (totalScore === best.score && signature.localeCompare(best.signature) < 0)) {
        best = { teams: candidateTeams, score: totalScore, signature };
      }
    }

    if (best) {
      memo.set(memoKey, best);
      return best;
    }

    const singleTeam: PracticeRotationTeam = { players: [player], source: 'single' };
    const tail = buildBestTeamSet(candidates.slice(1));
    const fallbackTeams = [singleTeam, ...tail.teams];
    const fallback = {
      teams: fallbackTeams,
      score: tail.score,
      signature: signatureForTeams(fallbackTeams),
    };
    memo.set(memoKey, fallback);
    return fallback;
  };

  teams.push(...buildBestTeamSet(remaining).teams);

  teams.forEach((team) => {
    if (team.players.length !== 2) return;
    const [player, partner] = team.players;
    const key = pairKey(player.playerId, partner.playerId);
    partnerCounts.set(key, (partnerCounts.get(key) || 0) + 1);
  });

  settings.mustPartner.forEach((rule) => {
    const key = pairKey(rule.player1Id, rule.player2Id);
    const bothPlaying = playing.filter((participant) => participant.playerId === rule.player1Id || participant.playerId === rule.player2Id).length === 2;
    const paired = teams.some((team) => team.players.length === 2 && pairKey(team.players[0].playerId, team.players[1].playerId) === key);
    if (bothPlaying && !paired) warnings.push('A required partner pair could not be made in one round because of court or pairing conflicts.');
  });

  return teams;
}

function courtTeamScore(teamA: PracticeRotationTeam | null, teamB: PracticeRotationTeam | null, settings: PracticeRotationSettings): number {
  if (settings.strengthMix === 'random') return 0;
  const averageA = teamAverageRanking(teamA);
  const averageB = teamAverageRanking(teamB);
  if (averageA == null || averageB == null) return 0;
  const averageDifference = Math.abs(averageA - averageB);
  const spread = teamRankingSpread(teamA) + teamRankingSpread(teamB);
  if (settings.strengthMix === 'balanced') return -averageDifference * 8 - spread * 2;
  if (settings.strengthMix === 'development') return -averageDifference * 8 + spread * 1.5;
  return -averageDifference * 8 + spread;
}

function assignCourts(teams: PracticeRotationTeam[], settings: PracticeRotationSettings): PracticeRotationCourt[] {
  const result: PracticeRotationCourt[] = [];
  const queue = [...teams];
  for (let courtNumber = 1; courtNumber <= settings.courts; courtNumber += 1) {
    let teamA: PracticeRotationTeam | null = null;
    let teamB: PracticeRotationTeam | null = null;
    if (queue.length <= 2) {
      teamA = queue.shift() || null;
      teamB = queue.shift() || null;
    } else {
      let best: { firstIndex: number; secondIndex: number; score: number; signature: string } | null = null;
      for (let firstIndex = 0; firstIndex < queue.length; firstIndex += 1) {
        for (let secondIndex = firstIndex + 1; secondIndex < queue.length; secondIndex += 1) {
          const score = courtTeamScore(queue[firstIndex], queue[secondIndex], settings);
          const signature = [
            queue[firstIndex].players.map((player) => player.name).sort().join('::'),
            queue[secondIndex].players.map((player) => player.name).sort().join('::'),
          ].sort().join('||');
          if (!best || score > best.score || (score === best.score && signature.localeCompare(best.signature) < 0)) {
            best = { firstIndex, secondIndex, score, signature };
          }
        }
      }
      if (best) {
        teamB = queue.splice(best.secondIndex, 1)[0] || null;
        teamA = queue.splice(best.firstIndex, 1)[0] || null;
      }
    }
    result.push({
      courtNumber,
      courtLabel: settings.courtLabels[courtNumber - 1] || String(courtNumber),
      teamA,
      teamB,
      note: teamA && !teamB ? 'Open court / drill court' : null,
    });
  }
  return result;
}

function consecutiveSitMax(rounds: PracticeRotationRound[]): number {
  const streaks = new Map<string, number>();
  const maxes = new Map<string, number>();
  rounds.forEach((round) => {
    const sittingKeys = new Set(round.sitting.map(practiceParticipantKey));
    const allKeys = new Set([
      ...round.sitting.map(practiceParticipantKey),
      ...round.courts.flatMap((court) => [...(court.teamA?.players || []), ...(court.teamB?.players || [])].map(practiceParticipantKey)),
    ]);
    allKeys.forEach((key) => {
      const next = sittingKeys.has(key) ? (streaks.get(key) || 0) + 1 : 0;
      streaks.set(key, next);
      maxes.set(key, Math.max(maxes.get(key) || 0, next));
    });
  });
  return Math.max(0, ...maxes.values());
}

function teamIsMixed(team: PracticeRotationTeam): boolean {
  return team.players.length === 2
    && team.players[0].gender !== 'unspecified'
    && team.players[1].gender !== 'unspecified'
    && team.players[0].gender !== team.players[1].gender;
}

function assignmentLabelForCourt(court: PracticeRotationCourt): string {
  return displayPracticeCourtLabel(court.courtLabel || court.courtNumber);
}

function participantDisplayName(participant: PracticeRotationParticipant | undefined, fallback: string): string {
  return participant?.name || fallback || 'Unknown player';
}

export function validatePracticeRotationPlan(plan: PracticeRotationPlan): PracticeRotationValidationResult {
  const settings = normalizeSettings(plan.settingsSnapshot);
  const participants = sortedParticipants(plan.participantSnapshot || []);
  const participantsByKey = new Map(participants.map((participant) => [practiceParticipantKey(participant), participant]));
  const errors: string[] = [];

  plan.rounds.forEach((round) => {
    const eligible = participants.filter((participant) => isAvailableForRound(participant.playerId, round.roundNumber, settings));
    const eligibleKeys = new Set(eligible.map(practiceParticipantKey));
    const playingAssignments = new Map<string, string[]>();
    const restingAssignments = new Map<string, string[]>();

    const recordAssignment = (target: Map<string, string[]>, participant: PracticeRotationParticipant, location: string) => {
      const key = practiceParticipantKey(participant);
      const values = target.get(key) || [];
      values.push(location);
      target.set(key, values);
    };

    round.courts.forEach((court) => {
      const courtLabel = assignmentLabelForCourt(court);
      [court.teamA, court.teamB].forEach((team) => {
        (team?.players || []).forEach((participant) => recordAssignment(playingAssignments, participant, courtLabel));
      });
    });
    (round.sitting || []).forEach((participant) => recordAssignment(restingAssignments, participant, 'Resting'));

    playingAssignments.forEach((locations, key) => {
      const participant = participantsByKey.get(key);
      const uniqueLocations = [...new Set(locations)];
      if (locations.length > 1) {
        if (uniqueLocations.length === 1) {
          errors.push(`Round ${round.roundNumber} invalid: ${participantDisplayName(participant, key)} appears twice on ${uniqueLocations[0]}.`);
        } else {
          errors.push(`Round ${round.roundNumber} invalid: ${participantDisplayName(participant, key)} is assigned to ${uniqueLocations.join(' and ')}.`);
        }
      }
      if (!eligibleKeys.has(key)) {
        errors.push(`Round ${round.roundNumber} invalid: ${participantDisplayName(participant, key)} is assigned to ${uniqueLocations[0] || 'a court'} but is not eligible for this round.`);
      }
    });

    restingAssignments.forEach((locations, key) => {
      const participant = participantsByKey.get(key);
      if (locations.length > 1) {
        errors.push(`Round ${round.roundNumber} invalid: ${participantDisplayName(participant, key)} appears twice in Resting.`);
      }
      if (!eligibleKeys.has(key)) {
        errors.push(`Round ${round.roundNumber} invalid: ${participantDisplayName(participant, key)} is listed as Resting but is not eligible for this round.`);
      }
    });

    eligible.forEach((participant) => {
      const key = practiceParticipantKey(participant);
      const playingLocations = playingAssignments.get(key) || [];
      const restingLocations = restingAssignments.get(key) || [];
      if (playingLocations.length > 0 && restingLocations.length > 0) {
        errors.push(`Round ${round.roundNumber} invalid: ${participant.name} is assigned to ${[...new Set(playingLocations)].join(' and ')} and Resting.`);
      }
      if (playingLocations.length === 0 && restingLocations.length === 0) {
        errors.push(`Round ${round.roundNumber} invalid: ${participant.name} has no assignment.`);
      }
    });
  });

  return { valid: errors.length === 0, errors };
}

export function assertValidPracticeRotationPlan(plan: PracticeRotationPlan): void {
  const validation = validatePracticeRotationPlan(plan);
  if (!validation.valid) {
    throw new Error(validation.errors.join('\n'));
  }
}

export function generatePracticeRotationPlan(input: PracticeRotationInput): PracticeRotationPlan {
  const participants = sortedParticipants(input.participants.filter((participant) => participant.name.trim()));
  const settings = normalizeSettings(input.settings);
  const warnings: string[] = [];
  const capacity = settings.courts * 4;
  const roundDuration = chooseRoundDuration(settings, participants.length);
  const rotationMinutes = Math.max(5, settings.durationMinutes - settings.warmupMinutes);
  const roundCount = Math.max(1, Math.floor(rotationMinutes / roundDuration));
  const playCounts = new Map(participants.map((participant) => [practiceParticipantKey(participant), 0]));
  const sitCounts = new Map(participants.map((participant) => [practiceParticipantKey(participant), 0]));
  const partnerCounts = new Map<string, number>();
  const upcomingPairSet = new Set((input.upcomingMatchPairs || []).map((rule) => pairKey(rule.player1Id, rule.player2Id)));
  const mustPairSet = new Set(settings.mustPartner.map((rule) => pairKey(rule.player1Id, rule.player2Id)));
  const avoidPairSet = new Set(settings.avoidPartner.map((rule) => pairKey(rule.player1Id, rule.player2Id)));
  const preferences = new Map((input.partnerPreferences || []).map((preference) => [pairKey(preference.player1Id, preference.player2Id), preference]));
  const rounds: PracticeRotationRound[] = [];
  const tiebreakerSeed = input.seed || `${Date.now()}:${Math.random()}`;
  let previousSitKeys = new Set<string>();

  if (participants.length === 0) warnings.push('No matched or unmatched Going attendees were available to schedule.');
  if (participants.length > capacity) warnings.push(`${participants.length - capacity} player${participants.length - capacity === 1 ? '' : 's'} must sit each full round because attendee count exceeds court capacity.`);

  for (let roundNumber = 1; roundNumber <= roundCount; roundNumber += 1) {
    const available = participants.filter((participant) => isAvailableForRound(participant.playerId, roundNumber, settings));
    const sitting = chooseSitters(available, roundNumber, settings, capacity, playCounts, sitCounts, previousSitKeys, tiebreakerSeed, warnings);
    const sittingKeys = new Set(sitting.map(practiceParticipantKey));
    const playing = available.filter((participant) => !sittingKeys.has(practiceParticipantKey(participant))).slice(0, capacity);
    sitting.forEach((participant) => {
      const key = practiceParticipantKey(participant);
      sitCounts.set(key, (sitCounts.get(key) || 0) + 1);
    });
    playing.forEach((participant) => {
      const key = practiceParticipantKey(participant);
      playCounts.set(key, (playCounts.get(key) || 0) + 1);
    });
    const teams = makeTeams(playing, settings, upcomingPairSet, mustPairSet, avoidPairSet, preferences, partnerCounts, warnings);
    rounds.push({
      roundNumber,
      startMinute: settings.warmupMinutes + (roundNumber - 1) * roundDuration,
      endMinute: settings.warmupMinutes + roundNumber * roundDuration,
      sitting: sitting.sort((a, b) => a.name.localeCompare(b.name)),
      courts: assignCourts(teams, settings),
    });
    previousSitKeys = sittingKeys;
  }

  const plays = [...playCounts.values()];
  const sits = [...sitCounts.values()];
  const playedSlots = rounds.reduce((total, round) => total + round.courts.reduce((courtTotal, court) => courtTotal + (court.teamA?.players.length || 0) + (court.teamB?.players.length || 0), 0), 0);
  const totalSlots = rounds.length * capacity;
  const teams = rounds.flatMap((round) => round.courts.flatMap((court) => [court.teamA, court.teamB].filter(Boolean) as PracticeRotationTeam[]));
  const doublesTeams = teams.filter((team) => team.players.length === 2);
  const mixedTeams = doublesTeams.filter(teamIsMixed);
  const upcomingPairsPracticed = [...upcomingPairSet].filter((key) => partnerCounts.has(key)).length;
  const uniquePartnerPercent = partnerCounts.size === 0 ? 100 : Math.round((partnerCounts.size / [...partnerCounts.values()].reduce((sum, count) => sum + count, 0)) * 100);
  const playSpread = plays.length ? Math.max(...plays) - Math.min(...plays) : 0;
  const sitSpread = sits.length ? Math.max(...sits) - Math.min(...sits) : 0;
  const maxConsecutiveSits = consecutiveSitMax(rounds);
  const utilization = totalSlots ? Math.round((playedSlots / totalSlots) * 100) : 0;
  const mixedPercent = doublesTeams.length ? Math.round((mixedTeams.length / doublesTeams.length) * 100) : 100;
  const qualityScore = Math.max(0, Math.min(100, Math.round(
    utilization * 0.35
    + (100 - playSpread * 14) * 0.2
    + (100 - sitSpread * 16) * 0.15
    + (100 - maxConsecutiveSits * 20) * 0.1
    + uniquePartnerPercent * 0.1
    + mixedPercent * 0.1,
  )));

  const plan: PracticeRotationPlan = {
    algorithmVersion: defaultPracticeRotationConfig.algorithmVersion,
    generatedAt: new Date().toISOString(),
    eventName: input.eventName,
    eventDate: input.eventDate,
    teamName: input.teamName,
    participantSnapshot: participants,
    rsvpSnapshotHash: practiceRsvpSnapshotHash(participants),
    settingsSnapshot: settings,
    rounds,
    metrics: {
      participantCount: participants.length,
      matchedParticipantCount: participants.filter((participant) => participant.matched).length,
      unmatchedParticipantCount: participants.filter((participant) => !participant.matched).length,
      courts: settings.courts,
      roundDurationMinutes: roundDuration,
      roundCount,
      warmupMinutes: settings.warmupMinutes,
      rotationMinutes,
      scheduledMinutes: roundCount * roundDuration,
      playCountMin: plays.length ? Math.min(...plays) : 0,
      playCountMax: plays.length ? Math.max(...plays) : 0,
      sitCountMin: sits.length ? Math.min(...sits) : 0,
      sitCountMax: sits.length ? Math.max(...sits) : 0,
      maxConsecutiveSits,
      courtUtilizationPercent: utilization,
      mixedTeamPercent: mixedPercent,
      uniquePartnerPercent,
      upcomingPairsPracticed,
      upcomingPairsAvailable: upcomingPairSet.size,
      qualityScore,
    },
    warnings: [...new Set(warnings)],
  };
  assertValidPracticeRotationPlan(plan);
  return plan;
}

function teamText(team: PracticeRotationTeam | null): string {
  if (!team) return 'Open';
  return team.players.map((player) => player.name).join(' / ');
}

function practiceRotationDisplayDate(value: string | null | undefined): string {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[2]}-${match[3]}-${match[1]}`;
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}-${day}-${date.getFullYear()}`;
}

function firstNameFromDisplayName(name: string): string {
  return name.trim().split(/\s+/)[0] || name.trim();
}

function lastInitialFromDisplayName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? `${parts[parts.length - 1][0].toUpperCase()}.` : '';
}

function practiceRotationShortNameMap(participants: PracticeRotationParticipant[]): Map<string, string> {
  const firstNameCounts = participants.reduce<Map<string, number>>((counts, participant) => {
    const key = firstNameFromDisplayName(participant.name).toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());
  return participants.reduce<Map<string, string>>((names, participant) => {
    const firstName = firstNameFromDisplayName(participant.name);
    const lastInitial = lastInitialFromDisplayName(participant.name);
    const duplicateFirstName = (firstNameCounts.get(firstName.toLowerCase()) || 0) > 1;
    names.set(practiceParticipantKey(participant), duplicateFirstName && lastInitial ? `${firstName} ${lastInitial}` : firstName);
    return names;
  }, new Map());
}

function shortTeamText(team: PracticeRotationTeam | null, shortNames: Map<string, string>): string {
  if (!team) return 'Open';
  const text = team.players.map((player) => shortNames.get(practiceParticipantKey(player)) || firstNameFromDisplayName(player.name)).join(' / ');
  const marker = team.source === 'upcoming_match' ? ' *' : '';
  return `${text}${marker}`;
}

export function practiceRotationPlanToText(plan: PracticeRotationPlan): string {
  assertValidPracticeRotationPlan(plan);
  const displayDate = practiceRotationDisplayDate(plan.eventDate);
  const lines = [
    `${plan.eventName || 'Practice'}${displayDate ? ` (${displayDate})` : ''}`,
    plan.teamName ? `Team: ${plan.teamName}` : '',
    `Warmup: ${plan.metrics.warmupMinutes} min • Rotation: ${plan.metrics.roundCount} rounds x ${plan.metrics.roundDurationMinutes} min`,
    '',
  ].filter(Boolean);
  plan.rounds.forEach((round) => {
    lines.push(`Round ${round.roundNumber} (${round.startMinute}-${round.endMinute} min, ${round.endMinute - round.startMinute} min)`);
    round.courts.forEach((court) => {
      lines.push(`${displayPracticeCourtLabel(court.courtLabel || court.courtNumber)}: ${teamText(court.teamA)} vs ${teamText(court.teamB)}`);
    });
    lines.push(`Sitting: ${round.sitting.length ? round.sitting.map((player) => player.name).join(', ') : 'None'}`);
    lines.push('');
  });
  if (plan.warnings.length) {
    lines.push('Notes:');
    plan.warnings.forEach((warning) => lines.push(`- ${warning}`));
  }
  return lines.join('\n').trim();
}

export function practiceRotationFilename(plan: PracticeRotationPlan): string {
  const compact = `${plan.teamName || 'CourtCaptain'} ${plan.eventName || 'Practice Rotation'} ${practiceRotationDisplayDate(plan.eventDate) || practiceRotationDisplayDate(new Date().toISOString().slice(0, 10))}`
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${compact}.pdf`;
}

export async function renderPracticeRotationPdf(plan: PracticeRotationPlan): Promise<Blob> {
  assertValidPracticeRotationPlan(plan);
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const pageWidth = 792;
  const pageHeight = 612;
  const margin = 30;
  const brand = rgb(0.04, 0.35, 0.09);
  const muted = rgb(0.35, 0.35, 0.35);
  const lightGreen = rgb(0.91, 0.96, 0.91);
  const headerBg = rgb(0.93, 0.96, 0.93);
  const restingBg = rgb(0.98, 0.94, 0.86);
  const border = rgb(0.70, 0.76, 0.70);
  const text = rgb(0.12, 0.12, 0.12);
  const displayDate = practiceRotationDisplayDate(plan.eventDate);
  const shortNames = practiceRotationShortNameMap(plan.participantSnapshot);
  const hasUpcomingMarker = plan.rounds.some((round) => round.courts.some((court) => court.teamA?.source === 'upcoming_match' || court.teamB?.source === 'upcoming_match'));
  const courts = Math.max(plan.metrics.courts, ...plan.rounds.flatMap((round) => round.courts.map((court) => court.courtNumber)), 1);
  const courtLabels = normalizePracticeCourtLabels(plan.settingsSnapshot?.courtLabels, courts);
  const footerHeight = 28;
  const headerHeight = 74;
  const tableTop = pageHeight - margin - headerHeight;
  const bottomLimit = margin + footerHeight;
  const availableTableHeight = tableTop - bottomLimit;
  const tableHeaderHeight = 30;
  const minimumRowHeight = 42;
  const desiredRowHeight = 52;
  const rowsPerPage = Math.max(1, Math.floor((availableTableHeight - tableHeaderHeight) / minimumRowHeight));
  const roundColumnWidth = 52;
  const restingColumnWidth = Math.max(92, Math.min(132, (pageWidth - margin * 2) * 0.16));
  const courtColumnWidth = (pageWidth - margin * 2 - roundColumnWidth - restingColumnWidth) / courts;
  const generatedAt = new Date(plan.generatedAt);
  const generatedText = Number.isNaN(generatedAt.getTime())
    ? plan.generatedAt
    : `${practiceRotationDisplayDate(generatedAt.toISOString().slice(0, 10))} ${generatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;

  const fitText = (value: string, maxWidth: number, size: number, font = regular): string => {
    if (font.widthOfTextAtSize(value, size) <= maxWidth) return value;
    let next = value;
    while (next.length > 3 && font.widthOfTextAtSize(`${next}...`, size) > maxWidth) next = next.slice(0, -1);
    return `${next.trim()}...`;
  };
  const drawCentered = (
    page: ReturnType<typeof pdf.addPage>,
    value: string,
    x: number,
    y: number,
    width: number,
    size: number,
    font = regular,
    color = text,
  ) => {
    const clipped = fitText(value, width - 8, size, font);
    page.drawText(clipped, {
      x: x + (width - font.widthOfTextAtSize(clipped, size)) / 2,
      y,
      size,
      font,
      color,
    });
  };
  const drawCell = (
    page: ReturnType<typeof pdf.addPage>,
    x: number,
    y: number,
    width: number,
    height: number,
    fill = rgb(1, 1, 1),
  ) => {
    page.drawRectangle({ x, y, width, height, color: fill, borderColor: border, borderWidth: 0.7 });
  };
  const drawTableHeader = (page: ReturnType<typeof pdf.addPage>, y: number) => {
    drawCell(page, margin, y, roundColumnWidth, tableHeaderHeight, headerBg);
    drawCentered(page, 'ROUND', margin, y + 10, roundColumnWidth, 9, bold, brand);
    for (let index = 0; index < courts; index += 1) {
      const x = margin + roundColumnWidth + courtColumnWidth * index;
      drawCell(page, x, y, courtColumnWidth, tableHeaderHeight, headerBg);
      drawCentered(page, displayPracticeCourtLabel(courtLabels[index]).toUpperCase(), x, y + 10, courtColumnWidth, 10, bold, brand);
    }
    const restingX = margin + roundColumnWidth + courtColumnWidth * courts;
    drawCell(page, restingX, y, restingColumnWidth, tableHeaderHeight, restingBg);
    drawCentered(page, 'RESTING', restingX, y + 10, restingColumnWidth, 10, bold, rgb(0.45, 0.25, 0.05));
  };
  const drawHeader = (page: ReturnType<typeof pdf.addPage>, pageNumber: number) => {
    page.drawText(plan.eventName || 'Practice Rotation', { x: margin, y: pageHeight - margin - 18, size: 18, font: bold, color: brand });
    const subtitle = [plan.teamName || 'CourtCaptain', displayDate].filter(Boolean).join(' • ');
    page.drawText(subtitle, { x: margin, y: pageHeight - margin - 36, size: 11, font: regular, color: muted });
    const summary = `${courts} court${courts === 1 ? '' : 's'} • ${plan.metrics.roundCount} round${plan.metrics.roundCount === 1 ? '' : 's'} • ${plan.metrics.roundDurationMinutes} min/round • ${plan.metrics.warmupMinutes} min warmup`;
    page.drawText(summary, { x: margin, y: pageHeight - margin - 54, size: 10, font: bold, color: text });
    if (pageNumber > 1) page.drawText(`continued • page ${pageNumber}`, { x: pageWidth - margin - 96, y: pageHeight - margin - 18, size: 9, font: regular, color: muted });
  };
  const drawFooter = (page: ReturnType<typeof pdf.addPage>, includeLegend: boolean) => {
    const instruction = 'When time expires: finish the point -> check the next round -> rotate immediately';
    page.drawText(instruction, { x: margin, y: margin + 20, size: 9, font: bold, color: brand });
    if (includeLegend) page.drawText('* Upcoming match partnership', { x: pageWidth - margin - 140, y: margin + 20, size: 8, font: regular, color: muted });
    page.drawText(`Generated by CourtCaptain • ${generatedText} • ${plan.metrics.participantCount} players`, { x: margin, y: margin + 6, size: 7.5, font: regular, color: muted });
  };

  for (let pageStart = 0, pageNumber = 1; pageStart < plan.rounds.length || (plan.rounds.length === 0 && pageStart === 0); pageStart += rowsPerPage, pageNumber += 1) {
    const pageRounds = plan.rounds.slice(pageStart, pageStart + rowsPerPage);
    const page = pdf.addPage([pageWidth, pageHeight]);
    drawHeader(page, pageNumber);
    const rowHeight = Math.min(desiredRowHeight, (availableTableHeight - tableHeaderHeight) / Math.max(1, pageRounds.length));
    drawTableHeader(page, tableTop - tableHeaderHeight);
    pageRounds.forEach((round, rowIndex) => {
      const y = tableTop - tableHeaderHeight - rowHeight * (rowIndex + 1);
      drawCell(page, margin, y, roundColumnWidth, rowHeight, lightGreen);
      drawCentered(page, String(round.roundNumber), margin, y + rowHeight / 2 - 7, roundColumnWidth, 16, bold, brand);
      for (let courtIndex = 0; courtIndex < courts; courtIndex += 1) {
        const x = margin + roundColumnWidth + courtColumnWidth * courtIndex;
        const court = round.courts.find((candidate) => candidate.courtNumber === courtIndex + 1) || null;
        drawCell(page, x, y, courtColumnWidth, rowHeight);
        const teamA = shortTeamText(court?.teamA || null, shortNames);
        const teamB = shortTeamText(court?.teamB || null, shortNames);
        drawCentered(page, teamA, x, y + rowHeight - 17, courtColumnWidth, 10.5, bold, text);
        drawCentered(page, 'vs', x, y + rowHeight / 2 - 4, courtColumnWidth, 8, regular, muted);
        drawCentered(page, teamB, x, y + 11, courtColumnWidth, 10.5, bold, text);
      }
      const restingX = margin + roundColumnWidth + courtColumnWidth * courts;
      drawCell(page, restingX, y, restingColumnWidth, rowHeight, restingBg);
      const resting = round.sitting.length
        ? round.sitting.map((player) => shortNames.get(practiceParticipantKey(player)) || firstNameFromDisplayName(player.name)).join(', ')
        : '-';
      drawCentered(page, resting, restingX, y + rowHeight / 2 - 5, restingColumnWidth, 10, bold, rgb(0.45, 0.25, 0.05));
    });
    drawFooter(page, hasUpcomingMarker);
    if (plan.rounds.length === 0) break;
  }

  pdf.setTitle(practiceRotationFilename(plan));
  pdf.setSubject('CourtCaptain Practice Rotation');
  const bytes = await pdf.save();
  const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return new Blob([arrayBuffer], { type: 'application/pdf' });
}
